import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { AttributeSelectorService } from 'src/app/reusables/attribute-selector/attribute-selector.service';
import { BaseGroupType } from 'src/app/reusables/models/group-type.model';
import { CXOneMultiSelectChanged, CXOneMultiSelectOption, MultiSelectArrays } from 'src/app/reusables/multi-select/multi-select.models';
import { AttributeCategoryInfo, AttributeCategoryModel, AttributeSelectorModel, AttributeType, SelectedCategoryModel} from 'src/shared/models/attribute-selector.models';
import { ModuleManager } from 'src/shared/models/module-manager';
import SlideoutSettingsManager from 'src/shared/models/slideout-settings-manager';

@Component({
    selector: 'cv-cxone-attribute-selector',
    templateUrl: './cxone-attribute-selector.component.html',
    styleUrls: ['./cxone-attribute-selector.component.scss']
})
export class CxoneAttributeSelectorComponent implements OnInit {

    @Input() pageName: string;
    @Input() settingsManager?: SlideoutSettingsManager;
    @Input() moduleManager: ModuleManager;
    @Output() categoryViewChanged = new EventEmitter<boolean>();
    @Output() selectedAttributesChanged = new EventEmitter<AttributeSelectorModel>();
    @Output() selectedLevelChanged = new EventEmitter<{ groupType: BaseGroupType; groupLevel: number; groupLevelName: string }>();
    @Output() attributeCategoriesCount = new EventEmitter<number>();

    categoryInfo: AttributeCategoryInfo = null;
    /** used to store the categories pull and is also used to populate @variable attributeCategories */
    categoriesModel: AttributeCategoryModel[] = null;
    /** master list of categories available */
    attributeCategories: CXOneMultiSelectOption[];
    /** list of which categories are currently selected usually created from master list by filtering on selected */
    categoriesSelected: CXOneMultiSelectOption[];
    /** used to identify if changes occured in child component as child component does not have access to category IDs */
    categoryNameIdMap = new Map<string, string>();

    /** constant used to limit the amount of categories that are selectable used in frontend html file */
    MAX_SELECTABLE = 5;

    /** passed through to the repeated selector component to populate each attribute selector */
    filledCategories: Map<string, MultiSelectArrays> = new Map();
    /** cache used so we only need to do a category pull once each time the settings are opened */
    cachedCategories: Map<string, MultiSelectArrays> = new Map();
    /** passed through to repeated selector component and used to know if all attributes have been loaded in yet or not */
    attributesLoading = true;
    /** used to store the state pull */
    statedAttributes: AttributeSelectorModel;

    private _openPromise: Promise<void> = null;
    private _resolveOpenPromise: () => void = null;
    private subscriptions: Subscription[] = [];
    viewByLimitsFilters = 0;

    constructor(
        public service: AttributeSelectorService,
        private changeDetector: ChangeDetectorRef,
    ) {

    }

    async ngOnInit() {
        await this.loadStatedAttributes();
        await this.pullAttributeCategories();
        this.setCategorySelector();
        await this.pullAttributes();
        this.attributeCategoriesCount.emit(this.attributeCategories.length);

        // if state exists update selectors accordingly from state
        if (this.statedAttributes.SelectedCategories.length > 0) {
            this.updateSelectorsFromState();
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s?.unsubscribe());
    }

    ngOnChanges(changes: SimpleChanges) {
        this.subscriptions = [];
        this.subscriptions.push(this.settingsManager.onSelectedGroupViewByChanged.subscribe(() => this.viewBySelectionChanged()));
        this.viewByLimitsFilters = this.moduleManager.container.moduleInfo.DTO.SettingViewByLimitsFilters;
    }

    updateSelectorsFromState() {
        // update attributeCategories.selected property from state
        this.statedAttributes.SelectedCategories.forEach(cat => {
            const found = this.attributeCategories.find(catState => catState.id === cat.SelectedCategoryID.toString());
            if (found) {
                found.selected = true;
            }
        });
        // recreate categoriesSelected from attributeCategories which will fill category selector
        this.categoriesSelected = this.attributeCategories.filter(cat => cat.selected);
        // update cachedCategories with selected attributes from state to keep in sync
        this.categoriesSelected.forEach(cat => {
            const cache = this.cachedCategories.get(cat.name);
            const attributesSelected = this.statedAttributes.SelectedCategories
                .find(catState => catState.SelectedCategoryID.toString() === cat.id).SelectedAttributeIDs;
            cache.options.forEach(cacheCat => {
                // since this component initializes with all selected turn off those which arent found in state
                if (!attributesSelected.find(s => s.toString() === cacheCat.id)) {
                    cacheCat.selected = false;
                };
            });
            cache.selection = cache.options.filter(cat1 => cat1.selected);
            // update filledCategories from updated cache which will populate the attribute selectors
            this.filledCategories.set(cat.name, cache);
        });
        this.changeDetector.detectChanges();
    }

    // async ngOnChanges(changes: SimpleChanges) {
    //     console.log(changes);
    // }

    onCategorySelectorOpen() {
        this._openPromise = new Promise(resolve => this._resolveOpenPromise = resolve);
    }

    onCategorySelectorClose(forceSaving = false) {
        if (this.checkIfChanges() || forceSaving) {
            this.save();
        }
    }

    /**
     * Saves selected categories to page state which will be loaded back in onInit
     */
    async save() {
        const categories = this.updateSelectedInCategory();
        let viewByID = null;
        if (this.settingsManager?.selectedToggle === BaseGroupType.DataAttribute) {
            viewByID = this.settingsManager?.selectedLevel;
        }
        const model: AttributeSelectorModel = {Type: AttributeType.Data, SelectedCategories: categories, GroupedCategoryID: viewByID};
        await this.service.saveSelectorModel(this.pageName, model);
    }

    updateSelectedInCategory(): SelectedCategoryModel[] {
        const categoriesModel: SelectedCategoryModel[] = [];
        for (const [key, value] of this.filledCategories) {
            const catID = +this.attributeCategories.find(cat => cat.name === key).id;
            const selectedIDs = value.selection.map(option => +option.id);
            categoriesModel.push({SelectedCategoryID: catID, SelectedAttributeIDs: selectedIDs} as SelectedCategoryModel);
        }
        return categoriesModel;
    }

    checkIfChanges() {
        const lastSavedIds = this.statedAttributes.SelectedCategories.map(cat => cat.SelectedCategoryID).sort().toString();
        const currentSelected = this.categoriesSelected.map(cat => cat.id).sort().toString();
        if (lastSavedIds !== currentSelected) {
            return true;
        }
        else {
            return false;
        }
    }

    onCategorySelectionChanged(change: CXOneMultiSelectChanged) {
        if (this.settingsManager?.selectedToggle === BaseGroupType.DataAttribute && this.viewByLimitsFilters > 0) {
            const viewByAttributeDeselected = change.selected.find(category => category.id === this.settingsManager?.selectedLevel?.toString()) ? false : true;
            if (viewByAttributeDeselected) {
                this.categoriesSelected.find(category => category.id === this.settingsManager?.selectedLevel?.toString()).selected = true;
                this.attributeCategories.find(category => category.id === this.settingsManager?.selectedLevel?.toString()).selected = true;
                change.selected.push(this.categoriesSelected.find(category => category.id === this.settingsManager?.selectedLevel?.toString()));
                this.changeDetector.detectChanges();
            }
        }

        this.categoriesSelected = change.selected.filter(x => x.selected);
        if (change.selected.length > this.filledCategories.size) { // added
            this.categoryAttributeAdded(change);
        }
        else { //removed
            this.categoryAttributeRemoved(change);
        }
    }

    private async loadStatedAttributes() {
        this.statedAttributes = await this.service.loadSelectorModel(this.pageName, AttributeType.Data);
    }

    private categoryAttributeAdded(c: CXOneMultiSelectChanged) {
        const arr = c.selected.filter((cat: CXOneMultiSelectOption) =>
            ![...this.filledCategories.keys()].find(selected => selected === cat.name));
        arr.forEach(category => {
            const multiSelectArrays: MultiSelectArrays =
                { options: this.cachedCategories.get(category.name).options.slice(), selection: this.cachedCategories.get(category.name).selection.slice() };
            this.filledCategories.set(category.name, multiSelectArrays);
        });
        this.queueChangeForChildComp();
    }

    private categoryAttributeRemoved(c: CXOneMultiSelectChanged) {
        const arr = [...this.filledCategories.keys()].filter((cat: string) => !c.selected.find(selected => selected.name === cat));
        arr.forEach(element => {
            this.filledCategories.delete(element);
            this.queueChangeForChildComp();
        });
    }

    private queueChangeForChildComp() {
        this.filledCategories = new Map(this.filledCategories);
    }

    private async pullAttributeCategories() {
        this.categoryInfo = await this.service.loadCategories(AttributeType.Data);
        this.categoriesModel = this.categoryInfo.AttributeCategoryModels;
    }

    /**
     * Used to fill the attribute category selector, runs after the data pull from
     * @function pullAttributeCategories()
     * defaults all options to false or not selected
     */
    private setCategorySelector() {
        this.attributeCategories = this.categoriesModel.map(catModel =>
            ({name: catModel.CategoryName, id: catModel.CategoryID.toString(), selected: false}));
        this.categoriesSelected = this.attributeCategories.filter(option => option.selected);
        this.attributeCategories.forEach(cat => this.categoryNameIdMap.set(cat.name, cat.id));
    }

    /** pulls and caches all attributes.
     * selected attributes will be updated in child component and saved to page state.
     * if state exists cachedCategories will be updated and then used to populate @variable
     * filledCategories which is used by child component to generate attribute selectors
     */
    private async pullAttributes() {
        for (const category of this.attributeCategories) {
            const attributeInfo = await this.service.loadValues(+category.id, AttributeType.Data);
            const attributeOptions: CXOneMultiSelectOption[] = []; // currently used for both options and selected in front end
            let attributesSelected: CXOneMultiSelectOption[] = [];
            attributeInfo.forEach(attribute => {
                // defaulting all attribute options to be selected when a category is selected
                attributeOptions.push({id: attribute.ID.toString(), name: attribute.Name, selected: true});
            });
            attributesSelected = attributeOptions.filter(option => option.selected);
            const multiSelectArrays: MultiSelectArrays = {options: attributeOptions, selection: attributesSelected};
            this.cachedCategories.set(category.name, multiSelectArrays); // caching attribute selectors
        };
        this.attributesLoading = false;
    }

    private viewBySelectionChanged() { //Add view by selected attribute as a selected data attribute
        if (this.settingsManager?.selectedToggle === BaseGroupType.DataAttribute && this.viewByLimitsFilters > 0) {
            if (this.categoriesSelected?.find(category => category.id === this.settingsManager?.selectedLevel?.toString())) {
                this.categoriesSelected = this.categoriesSelected.filter(category => category.id !== this.settingsManager?.selectedLevel?.toString());
                let change: CXOneMultiSelectChanged = {
                    deselected: [],
                    selectAll: false,
                    selected: this.categoriesSelected
                }
                this.categoryAttributeRemoved(change);
            } else if (this.categoriesSelected?.length === this.MAX_SELECTABLE) {
                this.categoriesSelected.pop();
                let change: CXOneMultiSelectChanged = {
                    deselected: [],
                    selectAll: false,
                    selected: this.categoriesSelected
                }
                this.categoryAttributeRemoved(change);
            }

            this.categoriesSelected.push(this.attributeCategories?.find(category => category.id === this.settingsManager?.selectedLevel?.toString()));
            this.categoriesSelected = this.categoriesSelected.slice(0); // Needed to force a selection reload in the cxone-multiselect-dropdown component.
            this.categoriesSelected.forEach(category => {
                category.selected = true;
            });
            let change: CXOneMultiSelectChanged = {
                deselected: [],
                selectAll: false,
                selected: this.categoriesSelected
            }
            this.categoryAttributeAdded(change);

            this.filledCategories.get(this.categoriesSelected.find(category => category.id === this.settingsManager?.selectedLevel?.toString()).name).selection = this.filledCategories.get(this.categoriesSelected.find(category => category.id === this.settingsManager?.selectedLevel?.toString()).name).selection.splice(0, this.viewByLimitsFilters);

            this.onCategorySelectorClose(true);
        }
    }
}
